import { createContext, type RefObject } from "react";

import type { MayaSpace } from "../spaces";
import type { Point } from "../utils/warp";

/**
 * The stardust warp between the launcher and the sidebar's avatar. `in`
 * flies from the launcher to the avatar (opening), `out` the other way;
 * `page` flies from the avatar into the AI Assistant page's composer (the
 * sidebar closing because Maya moved into that page). While `travelling`,
 * the destination stays hidden; once the stars land it is revealed while the
 * last sparks and the shockwave finish.
 */
export interface MayaWarp {
  id: number;
  direction: "in" | "out" | "page";
  /** Centre of the source, captured the moment the toggle happened. */
  from: Point;
  phase: "travelling" | "landed";
}

/** The full AI Assistant page's composer, registered while that page is
 *  open (see useMayaAssistantComposer). */
export interface MayaAssistantComposer {
  inputRef: RefObject<HTMLTextAreaElement | null>;
}

export interface MayaSidebarContextValue {
  /** The chat sidebar is open beside the page. */
  open: boolean;
  toggle: () => void;
  close: () => void;
  /** When true, each turn tells Maya which page is open beside the sidebar. */
  sharePageContext: boolean;
  toggleSharePageContext: () => void;
  /** The floating launcher — focus returns here when the sidebar closes. */
  triggerRef: RefObject<HTMLButtonElement | null>;
  /** The sidebar's <aside>, to tell whether focus was inside it on close. */
  sidebarRef: RefObject<HTMLElement | null>;
  /** Set on a close that took focus out of the sidebar; the launcher takes
   *  focus back once it is visible again (after the warp lands). */
  returnFocusRef: RefObject<boolean>;
  /** Set when the user opens the sidebar — as opposed to it being restored
   *  on reload — so only a deliberate open moves focus into the composer. */
  focusComposerRef: RefObject<boolean>;
  /** Maya's avatar in the sidebar header — the warp's other end. */
  headerOrbRef: RefObject<HTMLSpanElement | null>;
  /** The open/close animation in flight, if any. */
  warp: MayaWarp | null;
  /** The stars reached the destination: reveal it. */
  landWarp: (id: number) => void;
  /** The animation finished: drop it. */
  clearWarp: (id: number) => void;
  /** My Space or Workspace — follows the page (see MayaSpace). */
  space: MayaSpace;
  /** The full AI Assistant page is open. Maya is already there, so the
   *  sidebar stays shut and the launcher just says so. */
  onAssistantPage: boolean;
  /** The AI Assistant page's composer, while that page is open. */
  assistantComposerRef: RefObject<MayaAssistantComposer | null>;
  /** Set on the AI Assistant page: the sidebar's chat may have moved on
   *  there, so it is re-read from the server the next time it opens. */
  staleChatRef: RefObject<boolean>;
}

export const MayaSidebarContext = createContext<MayaSidebarContextValue | null>(
  null,
);

/** The AI Assistant page registers its composer through this, not the
 *  sidebar context: the ref never changes, so that page doesn't re-render
 *  whenever the sidebar does. */
export const MayaAssistantComposerContext =
  createContext<RefObject<MayaAssistantComposer | null> | null>(null);
